/**
 * Gießplan: berechnet für jede Aufgabe, wann sie fällig ist – und warum.
 *
 * Grundidee: Die App erinnert ans PRÜFEN (Fingertest), nicht blind ans Gießen.
 * Der Abstand = Grundwert × Faktoren (Topf, Gelerntes, Winter, trockene Luft).
 * Jeder Faktor hat einen Text, damit die App bei „Warum?“ alles erklären kann.
 */
import { allTaskTypes, findSpecies } from './catalog';
import { fmtDateShort, fmtDays, fmtFactor, fmtHours, fmtNumber } from './format';
import type { AppState, CareEvent, CareTask, Plant, Settings, Soil, Species, TaskType } from './model';
import { SOIL_RANK } from './model';
import { dayLengthHours } from './sun';
import { addDaysToKey, dayKeyDiff, localDateKey, localTimeOnDay, zonedToUtc } from './time';

/** Tage kürzer als das → Winterpause für Dünger & Co. */
export const WINTER_PAUSE_BELOW_H = 10.5;
/** Ab hier (und kürzer) werden Gieß-Abstände gestreckt. */
export const WINTER_STRETCH_BELOW_H = 12;

export const LEARN = {
  /** „Noch feucht“ → Abstand +12 % */
  stillMoist: 1.12,
  /** Erde trockener als ideal → Abstand −10 % */
  tooDry: 0.9,
  min: 0.5,
  max: 2.5,
} as const;

/** Geschätztes Raumklima (aus dem Wetter berechnet). */
export interface Climate {
  /** Geschätzte Luftfeuchte drinnen in %, null = unbekannt. */
  indoorRh: number | null;
}

export interface IntervalFactor {
  key: 'pot' | 'learned' | 'winter' | 'dry_air';
  factor: number;
  label: string;
}

export type TaskState = 'overdue' | 'due' | 'soon' | 'ok' | 'paused' | 'snoozed';

export interface TaskStatus {
  task: CareTask;
  type: TaskType;
  plant: Plant;
  species: Species | undefined;
  /** Grundabstand ohne Faktoren. */
  baseDays: number;
  baseSource: string;
  factors: IntervalFactor[];
  /** Abstand mit allen Faktoren. */
  intervalDays: number;
  /** Letztes „erledigt“ oder „noch feucht“ oder „ausgelassen“. */
  lastEvent: CareEvent | null;
  lastDone: CareEvent | null;
  /** Wie oft hintereinander zuletzt „noch feucht“. */
  moistStreak: number;
  /** Fällig am (lokaler Tag, JJJJ-MM-TT); null = pausiert ohne Ende. */
  dueKey: string | null;
  /** Tage bis fällig (negativ = überfällig); null = pausiert. */
  daysUntilDue: number | null;
  state: TaskState;
  pausedReason: string | null;
  snoozedUntil: string | null;
  /** Heute schon erledigt/geprüft? */
  handledToday: boolean;
  /** Erklärung für „Warum?“ – eine Zeile pro Punkt. */
  why: string[];
}

const ANCHOR_OUTCOMES = new Set(['done', 'still_moist', 'skipped']);

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/** Aktive (nicht gelöschte) Ereignisse einer Aufgabe, älteste zuerst. */
export function eventsForTask(events: CareEvent[], taskId: string): CareEvent[] {
  return events
    .filter((e) => e.taskId === taskId && !e.deletedAt)
    .sort((a, b) => a.at.localeCompare(b.at));
}

/**
 * Lernfaktor aus den Fingertests: „noch feucht“ → länger warten,
 * „trockener als ideal“ → etwas früher prüfen. Ohne Fingertest (z. B. NFC) keine Änderung.
 */
export function learnedFactor(events: CareEvent[], idealSoil: Soil): { factor: number; moist: number; dry: number } {
  let f = 1;
  let moist = 0;
  let dry = 0;
  for (const e of events) {
    if (e.outcome === 'still_moist') {
      f *= LEARN.stillMoist;
      moist++;
    } else if (e.outcome === 'done' && e.soil && SOIL_RANK[e.soil] < SOIL_RANK[idealSoil]) {
      f *= LEARN.tooDry;
      dry++;
    }
    f = clamp(f, LEARN.min, LEARN.max);
  }
  return { factor: round2(f), moist, dry };
}

/** Kürzester Tag des Jahres an diesem Breitengrad (Wien ≈ 8,4 h). */
export function shortestDayHours(lat: number): number {
  const year = new Date().getUTCFullYear();
  const solstice = new Date(Date.UTC(year, lat >= 0 ? 11 : 5, 21, 12));
  return dayLengthHours(solstice, lat);
}

/** Winter-Streckfaktor: 1 ab 12 h Tageslänge, bis 1 + stretch am kürzesten Tag. */
export function winterFactor(dayHours: number, stretch: number, lat: number): number {
  const minH = shortestDayHours(lat);
  const span = WINTER_STRETCH_BELOW_H - minH;
  if (span <= 0) return 1;
  const w = clamp((WINTER_STRETCH_BELOW_H - dayHours) / span, 0, 1);
  return round2(1 + stretch * w);
}

/** Faktor bei trockener Heizungsluft (nur für Aufgaben mit dryAirBoost). */
export function dryAirFactor(indoorRh: number | null): number {
  if (indoorRh == null) return 1;
  if (indoorRh < 35) return 0.5;
  if (indoorRh < 45) return 0.75;
  return 1;
}

/** Erster Tag ab `fromKey`, an dem die Tage wieder lang genug sind (max. 1 Jahr). */
export function winterPauseEnd(fromKey: string, lat: number, tz: string): string | null {
  for (let i = 0; i <= 366; i++) {
    const key = addDaysToKey(fromKey, i);
    if (dayLengthHours(localTimeOnDay(key, '12:00', tz), lat) >= WINTER_PAUSE_BELOW_H) return key;
  }
  return null;
}

function baseInterval(task: CareTask, type: TaskType, species: Species | undefined): { days: number; source: string } {
  if (task.intervalDays != null) return { days: task.intervalDays, source: 'eigener Wert' };
  const fromSpecies = species?.tasks[task.typeKey];
  if (fromSpecies != null) return { days: fromSpecies, source: `Steckbrief ${species!.name}` };
  if (task.typeKey === 'water' && species) return { days: species.waterDays, source: `Steckbrief ${species.name}` };
  return { days: type.defaultIntervalDays, source: `Standard für „${type.label}“` };
}

function fallbackType(key: string): TaskType {
  return {
    key,
    label: key,
    action: key,
    doneLabel: 'Erledigt',
    icon: '✅',
    defaultIntervalDays: 7,
    fingerTest: false,
    learn: false,
    winter: 'none',
    firstDue: 'after_interval',
    points: 5,
    dryAirBoost: false,
    description: '',
    builtin: false,
  };
}

export interface ScheduleContext {
  now: Date;
  settings: Settings;
  taskTypes: TaskType[];
  species: Species[];
  climate?: Climate;
}

/** Status einer einzelnen Aufgabe. */
export function taskStatus(task: CareTask, plant: Plant, events: CareEvent[], ctx: ScheduleContext): TaskStatus {
  const { now, settings } = ctx;
  const tz = settings.timezone;
  const lat = settings.location.lat;
  const todayKey = localDateKey(now, tz);
  const types = allTaskTypes(ctx.taskTypes);
  const type = types.find((t) => t.key === task.typeKey) ?? fallbackType(task.typeKey);
  const species = findSpecies(plant.speciesKey, ctx.species);
  const own = eventsForTask(events, task.id);
  const anchors = own.filter((e) => ANCHOR_OUTCOMES.has(e.outcome));
  const lastEvent = anchors.at(-1) ?? null;
  const lastDone = [...anchors].reverse().find((e) => e.outcome === 'done') ?? null;

  let moistStreak = 0;
  for (let i = anchors.length - 1; i >= 0 && anchors[i]!.outcome === 'still_moist'; i--) moistStreak++;

  const base = baseInterval(task, type, species);
  const factors: IntervalFactor[] = [];
  const why: string[] = [`Grundwert: ${fmtDays(base.days)} (${base.source})`];

  if (task.typeKey === 'water' && task.intervalDays == null) {
    if (plant.potMaterial === 'terracotta') factors.push({ key: 'pot', factor: 0.85, label: 'Tontopf trocknet schneller' });
    if (plant.potMaterial === 'self_watering') factors.push({ key: 'pot', factor: 1.5, label: 'Topf mit Wasserspeicher' });
  }

  if (type.learn) {
    const learned = learnedFactor(own, species?.idealSoil ?? 'dry');
    if (learned.factor !== 1) {
      const parts: string[] = [];
      if (learned.moist) parts.push(`${learned.moist}× war die Erde noch feucht`);
      if (learned.dry) parts.push(`${learned.dry}× war sie schon zu trocken`);
      factors.push({ key: 'learned', factor: learned.factor, label: `Gelernt: ${parts.join(', ')}` });
    }
  }

  const dayH = dayLengthHours(localTimeOnDay(todayKey, '12:00', tz), lat);
  let pausedReason: string | null = null;
  let pauseEndKey: string | null = null;

  if (type.winter === 'stretch') {
    const wf = winterFactor(dayH, species?.winterStretch ?? 0.4, lat);
    if (wf !== 1) factors.push({ key: 'winter', factor: wf, label: `Winter: Tage nur ${fmtHours(dayH)} lang` });
  } else if (type.winter === 'pause' && dayH < WINTER_PAUSE_BELOW_H) {
    pauseEndKey = winterPauseEnd(todayKey, lat, tz);
    pausedReason = `Winterpause – die Tage sind kürzer als ${fmtNumber(WINTER_PAUSE_BELOW_H, 1)} h`;
  }

  if (type.dryAirBoost) {
    const df = dryAirFactor(ctx.climate?.indoorRh ?? null);
    if (df !== 1) {
      factors.push({ key: 'dry_air', factor: df, label: `Trockene Heizungsluft (drinnen ca. ${Math.round(ctx.climate!.indoorRh!)} %)` });
    }
  }

  const product = factors.reduce((p, f) => p * f.factor, 1);
  const intervalDays = clamp(round2(base.days * product), 0.5, 730);
  for (const f of factors) why.push(`${f.label}: ${fmtFactor(f.factor)}`);
  if (factors.length) why.push(`Ergibt: ${fmtDays(intervalDays)}`);

  // Fälligkeit bestimmen
  let dueKey: string;
  if (!lastEvent) {
    const createdKey = localDateKey(new Date(task.createdAt), tz);
    dueKey = type.firstDue === 'now' ? createdKey : addDaysToKey(createdKey, Math.max(1, Math.round(intervalDays)));
  } else {
    const lastKey = localDateKey(new Date(lastEvent.at), tz);
    if (lastEvent.outcome === 'still_moist') {
      const recheck = clamp(Math.round(intervalDays * 0.3), 1, 4);
      dueKey = addDaysToKey(lastKey, recheck);
      why.push(`Zuletzt noch feucht → in ${recheck} ${recheck === 1 ? 'Tag' : 'Tagen'} nochmal prüfen`);
    } else {
      dueKey = addDaysToKey(lastKey, Math.max(1, Math.round(intervalDays)));
    }
  }

  let state: TaskState;
  let finalDue: string | null = dueKey;

  if (task.pausedUntil && dayKeyDiff(todayKey, task.pausedUntil) >= 0) {
    state = 'paused';
    pausedReason = `Pausiert bis ${fmtDateShort(localTimeOnDay(task.pausedUntil, '12:00', tz), tz)}`;
    const resume = addDaysToKey(task.pausedUntil, 1);
    if (dayKeyDiff(dueKey, resume) > 0) finalDue = resume;
  } else if (pausedReason) {
    state = 'paused';
    finalDue = pauseEndKey && dayKeyDiff(dueKey, pauseEndKey) > 0 ? pauseEndKey : pauseEndKey ? dueKey : null;
    if (pauseEndKey) {
      why.push(`${pausedReason}. Geht weiter ab ca. ${fmtDateShort(localTimeOnDay(pauseEndKey, '12:00', tz), tz)}`);
    } else {
      why.push(pausedReason);
    }
  } else {
    const d = dayKeyDiff(todayKey, dueKey);
    state = d < 0 ? 'overdue' : d === 0 ? 'due' : d <= 2 ? 'soon' : 'ok';
  }

  // „Später erinnern“: gilt, solange danach nichts erledigt wurde
  let snoozedUntil: string | null = null;
  const lastSnooze = [...own].reverse().find((e) => e.outcome === 'snoozed');
  if (lastSnooze?.snoozeUntil && (!lastEvent || lastSnooze.at > lastEvent.at) && new Date(lastSnooze.snoozeUntil) > now) {
    snoozedUntil = lastSnooze.snoozeUntil;
    if (state === 'due' || state === 'overdue') state = 'snoozed';
  }

  if (!task.enabled) {
    state = 'paused';
    pausedReason = 'Ausgeschaltet';
    finalDue = null;
  }

  const handledToday = !!lastEvent && localDateKey(new Date(lastEvent.at), tz) === todayKey;
  if (finalDue && state !== 'paused') why.push(`Nächstes Mal: ${fmtDateShort(localTimeOnDay(finalDue, '12:00', tz), tz)}`);

  return {
    task,
    type,
    plant,
    species,
    baseDays: base.days,
    baseSource: base.source,
    factors,
    intervalDays,
    lastEvent,
    lastDone,
    moistStreak,
    dueKey: finalDue,
    daysUntilDue: finalDue ? dayKeyDiff(todayKey, finalDue) : null,
    state,
    pausedReason,
    snoozedUntil,
    handledToday,
    why,
  };
}

/** Status aller aktiven Aufgaben (archivierte Pflanzen ausgenommen), dringendste zuerst. */
export function allStatuses(state: AppState, now: Date, climate?: Climate): TaskStatus[] {
  const ctx: ScheduleContext = { now, settings: state.settings, taskTypes: state.taskTypes, species: state.species, climate };
  const plants = new Map(state.plants.filter((p) => !p.archivedAt).map((p) => [p.id, p]));
  const out: TaskStatus[] = [];
  for (const task of state.tasks) {
    const plant = plants.get(task.plantId);
    if (!plant) continue;
    out.push(taskStatus(task, plant, state.events, ctx));
  }
  return out.sort(compareStatus);
}

const STATE_ORDER: Record<TaskState, number> = { overdue: 0, due: 1, snoozed: 2, soon: 3, ok: 4, paused: 5 };

export function compareStatus(a: TaskStatus, b: TaskStatus): number {
  return (
    STATE_ORDER[a.state] - STATE_ORDER[b.state] ||
    (a.daysUntilDue ?? 9999) - (b.daysUntilDue ?? 9999) ||
    a.plant.name.localeCompare(b.plant.name, 'de') ||
    a.type.label.localeCompare(b.type.label, 'de')
  );
}

/** Aufgaben, um die man sich heute kümmern sollte (fällig/überfällig, nicht vertagt, heute noch nicht erledigt). */
export function openToday(statuses: TaskStatus[]): TaskStatus[] {
  return statuses.filter((s) => (s.state === 'due' || s.state === 'overdue') && !s.handledToday);
}

/** Zahl fürs App-Symbol. */
export function badgeCount(statuses: TaskStatus[]): number {
  return openToday(statuses).length;
}

/** Nach dem Erledigen: wann ist die Aufgabe als Nächstes dran? (für die Bestätigung „Nächstes Mal: …“) */
export function previewNextDue(status: TaskStatus, outcome: 'done' | 'still_moist' | 'skipped', now: Date, tz: string): string {
  const todayKey = localDateKey(now, tz);
  if (outcome === 'still_moist') {
    const recheck = clamp(Math.round(status.intervalDays * LEARN.stillMoist * 0.3), 1, 4);
    return addDaysToKey(todayKey, recheck);
  }
  return addDaysToKey(todayKey, Math.max(1, Math.round(status.intervalDays)));
}

/** Übergießen-Schutz: zuletzt mehrfach „noch feucht“? Dann vor dem Gießen einmal nachfragen. */
export function overwaterWarning(status: TaskStatus): string | null {
  if (status.task.typeKey !== 'water') return null;
  if (status.moistStreak >= 2) {
    return `Die letzten ${status.moistStreak} Fingertests sagten „noch feucht“. Wirklich schon gießen?`;
  }
  return null;
}

/** Zeitpunkt „heute um HH:MM“ in der Zeitzone der Einstellungen. */
export function todayAt(now: Date, hhmm: string, tz: string): Date {
  const key = localDateKey(now, tz);
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  const [h, min] = hhmm.split(':').map(Number) as [number, number];
  return zonedToUtc(y, m, d, h, min, tz);
}
